import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Thermometer, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

/**
 * Staff version of the Temp Logs widget.
 * Shows today's 6 slots, the pending one (if any), and a big CTA to submit.
 */
export default function MyTempLogsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiRequest('/temperature-logs/me/today-status');
      setData(d);
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(i);
  }, [load]);

  const slots = data?.slots || [];
  const pending = data?.pending_slot;
  const done = slots.filter(s => s.done).length;

  return (
    <Card data-testid="my-temp-logs-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Thermometer className="h-4 w-4 text-blue-600" />
          My Temp Logs Today
          <Badge variant="outline" className="ml-auto text-xs">{done}/{slots.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.store ? (
          <p className="text-xs text-muted-foreground">No store assigned.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {slots.map(s => (
                <Badge
                  key={s.hour}
                  variant={s.done ? 'default' : (s.is_current ? 'destructive' : 'outline')}
                  className={s.done ? 'bg-green-600 hover:bg-green-700' : ''}
                  data-testid={`my-temp-slot-${s.hour}`}
                >
                  {s.done && <Check className="h-3 w-3 mr-1" />}
                  {s.label}
                </Badge>
              ))}
            </div>
            {pending !== null && pending !== undefined ? (
              <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {String(pending).padStart(2, '0')}:00 slot is pending
              </div>
            ) : (
              <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <Check className="h-3 w-3" /> All up to date
              </div>
            )}
            <Button
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/temp-logs/new')}
              data-testid="my-temp-submit-btn"
            >
              Submit a temp check
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
